import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, ShieldOff, QrCode, KeyRound, Smartphone, Copy, Check, Printer, Trash2, RefreshCw } from 'lucide-react';
import { API } from '@/utils/constants';

/**
 * TwoFactorPanel
 *
 * Self-contained Settings panel for managing 2FA:
 * - Enable / disable TOTP (Google / Microsoft Authenticator etc.)
 * - View, copy and print 10 recovery codes
 * - Manage recovery email + phone (used as email/SMS OTP fallback)
 * - Revoke trusted devices (per-browser, 30-day "remember this device")
 */
export const TwoFactorPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Setup wizard state
  const [setupStep, setSetupStep] = useState(null); // null | 'qr' | 'codes'
  const [setupData, setSetupData] = useState(null);  // { secret, qr_code_data_url, provisioning_uri }
  const [setupCode, setSetupCode] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Disable dialog state
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  // Regenerate dialog state
  const [regenCode, setRegenCode] = useState('');
  const [showRegen, setShowRegen] = useState(false);

  const refreshStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`);
      setStatus(data);
      setRecoveryEmail(data.recovery_email || '');
      setRecoveryPhone(data.recovery_phone || '');
    } catch (e) {
      toast.error('Failed to load 2FA status');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/2fa/status`);
        if (cancelled) return;
        setStatus(data);
        setRecoveryEmail(data.recovery_email || '');
        setRecoveryPhone(data.recovery_phone || '');
      } catch (e) {
        if (!cancelled) toast.error('Failed to load 2FA status');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup/start`);
      setSetupData(data);
      setSetupStep('qr');
      setSetupCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async () => {
    if (!setupCode || setupCode.trim().length < 6) {
      toast.error('Enter the 6-digit code from your authenticator');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup/verify`, {
        secret: setupData.secret,
        code: setupCode.trim(),
        recovery_email: recoveryEmail || undefined,
        recovery_phone: recoveryPhone || undefined,
      });
      setRecoveryCodes(data.recovery_codes);
      setSetupStep('codes');
      toast.success('2FA enabled. Save your recovery codes!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify code');
    } finally {
      setLoading(false);
    }
  };

  const finishSetup = async () => {
    setSetupStep(null);
    setSetupData(null);
    setSetupCode('');
    setRecoveryCodes(null);
    await refreshStatus();
  };

  const saveContact = async () => {
    try {
      await axios.put(`${API}/auth/2fa/contact`, {
        recovery_email: recoveryEmail || null,
        recovery_phone: recoveryPhone || null,
      });
      toast.success('Recovery contact saved');
      refreshStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const disable2FA = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password: disablePassword, code: disableCode });
      toast.success('2FA disabled');
      setShowDisable(false);
      setDisablePassword('');
      setDisableCode('');
      refreshStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const regenerateCodes = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/regenerate-recovery`, { code: regenCode });
      setRecoveryCodes(data.recovery_codes);
      setSetupStep('codes');
      setShowRegen(false);
      setRegenCode('');
      refreshStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not regenerate codes');
    } finally {
      setLoading(false);
    }
  };

  const revokeDevice = async (deviceId) => {
    try {
      await axios.delete(`${API}/auth/2fa/trusted-devices/${deviceId}`);
      toast.success('Device revoked');
      refreshStatus();
    } catch (e) {
      toast.error('Failed to revoke device');
    }
  };

  const revokeAllDevices = async () => {
    try {
      await axios.delete(`${API}/auth/2fa/trusted-devices`);
      toast.success('All trusted devices revoked');
      refreshStatus();
    } catch (e) {
      toast.error('Failed to revoke devices');
    }
  };

  const copyAllCodes = async () => {
    if (!recoveryCodes) return;
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    } catch { /* ignore */ }
  };

  const copySecret = async () => {
    if (!setupData?.secret) return;
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 1500);
    } catch { /* ignore */ }
  };

  const printCodes = () => {
    if (!recoveryCodes) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>2FA Recovery Codes</title>
      <style>body{font-family:monospace;padding:32px;max-width:560px;margin:auto}h1{font-family:system-ui;font-size:20px;margin-bottom:8px}p{font-family:system-ui;color:#475569}ol{font-size:18px;line-height:2}.warn{background:#fef3c7;padding:12px;border-radius:6px;border:1px solid #fcd34d;font-family:system-ui;margin:16px 0}</style>
      </head><body>
      <h1>Two-Factor Recovery Codes</h1>
      <p>Total Data Recall &middot; Generated ${new Date().toLocaleString()}</p>
      <div class="warn">Each code can only be used once. Store this in a safe place (locked drawer, password manager).</div>
      <ol>${recoveryCodes.map(c => `<li>${c}</li>`).join('')}</ol>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  if (!status) {
    return <Card><CardContent style={{ padding: '2rem' }}>Loading 2FA status...</CardContent></Card>;
  }

  // ====== SETUP WIZARD: QR step ======
  if (setupStep === 'qr' && setupData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <QrCode size={20} /> Set up Two-Factor Authentication
          </CardTitle>
          <CardDescription>Scan the QR code with Google Authenticator, Microsoft Authenticator or Authy</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 240px' }}>
              <img src={setupData.qr_code_data_url} alt="2FA QR code" style={{ width: '240px', height: '240px', border: '1px solid #e2e8f0', borderRadius: '8px' }} data-testid="2fa-qr-image" />
            </div>
            <div style={{ flex: '1 1 280px', minWidth: '260px' }}>
              <p style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                1. Open your authenticator app and tap <strong>+ Add</strong>.<br />
                2. Choose <strong>Scan QR code</strong> and point it at this screen.<br />
                3. Enter the 6-digit code your app generates below.
              </p>
              <div style={{ marginBottom: '0.75rem' }}>
                <Label style={{ fontSize: '0.8rem', color: '#64748b' }}>Can&apos;t scan? Enter this key manually:</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <code style={{ flex: 1, background: '#f1f5f9', padding: '8px 10px', borderRadius: '6px', fontSize: '0.85rem', wordBreak: 'break-all' }}>{setupData.secret}</code>
                  <Button type="button" variant="outline" size="sm" onClick={copySecret} data-testid="2fa-copy-secret-btn">
                    {copiedSecret ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <Label htmlFor="setupCode">Authenticator code</Label>
                <Input
                  id="setupCode"
                  type="text"
                  inputMode="numeric"
                  placeholder="123 456"
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value)}
                  data-testid="2fa-setup-code-input"
                  style={{ fontSize: '1.4rem', letterSpacing: '0.3em', textAlign: 'center' }}
                />
              </div>

              <details style={{ marginTop: '1.25rem' }}>
                <summary style={{ cursor: 'pointer', color: '#475569', fontSize: '0.875rem' }}>Add backup contact (optional but recommended)</summary>
                <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
                  <div>
                    <Label htmlFor="rec-email">Recovery email (for email OTP fallback)</Label>
                    <Input id="rec-email" type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} placeholder="you@personal.com" data-testid="2fa-recovery-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="rec-phone">Recovery phone (for SMS OTP fallback)</Label>
                    <Input id="rec-phone" type="tel" value={recoveryPhone} onChange={(e) => setRecoveryPhone(e.target.value)} placeholder="+614XXXXXXXX" data-testid="2fa-recovery-phone-input" />
                  </div>
                </div>
              </details>

              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                <Button onClick={confirmSetup} disabled={loading} data-testid="2fa-confirm-btn">
                  {loading ? 'Verifying...' : 'Verify & enable 2FA'}
                </Button>
                <Button variant="outline" onClick={() => { setSetupStep(null); setSetupData(null); }}>Cancel</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ====== SETUP WIZARD: Recovery codes step ======
  if (setupStep === 'codes' && recoveryCodes) {
    return (
      <Card>
        <CardHeader>
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <KeyRound size={20} /> Save your recovery codes
          </CardTitle>
          <CardDescription>You will not see these again. Store them in a password manager or print them.</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', padding: '12px 14px', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>
            <strong>⚠ Each code can only be used once.</strong> Use them to sign in if you ever lose access to your authenticator app.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {recoveryCodes.map((c) => (
              <code key={c} style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '6px', textAlign: 'center', fontSize: '1.05rem', letterSpacing: '0.05em' }}>{c}</code>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button variant="outline" onClick={copyAllCodes} data-testid="2fa-copy-codes-btn">
              {copiedAll ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />} Copy all
            </Button>
            <Button variant="outline" onClick={printCodes} data-testid="2fa-print-codes-btn">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button onClick={finishSetup} data-testid="2fa-finish-setup-btn">I&apos;ve saved them — done</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ====== DEFAULT VIEW ======
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {status.enabled ? <ShieldCheck size={20} color="#16a34a" /> : <ShieldOff size={20} color="#94a3b8" />}
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          {status.enabled
            ? 'Active — your account requires a second factor at sign-in.'
            : 'Add a second layer of protection using a code from your phone.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!status.enabled && (
          <div>
            <p style={{ marginBottom: '1rem', color: '#475569' }}>
              When enabled, sign-in requires your password <em>plus</em> a 6-digit code from Google Authenticator, Microsoft Authenticator or Authy. Recovery codes and email/SMS fallbacks keep you from getting locked out.
            </p>
            <Button onClick={startSetup} disabled={loading} data-testid="2fa-enable-btn">
              <ShieldCheck size={16} className="mr-1.5" /> {loading ? 'Loading...' : 'Enable 2FA'}
            </Button>
          </div>
        )}

        {status.enabled && (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              <div>
                <div style={{ fontWeight: 600, color: '#166534' }}>2FA is active</div>
                <div style={{ fontSize: '0.85rem', color: '#15803d' }}>{status.recovery_codes_remaining} of 10 recovery codes remaining</div>
              </div>
              <Button variant="outline" onClick={() => setShowRegen(true)} data-testid="2fa-regen-btn">
                <RefreshCw size={14} className="mr-1" /> Regenerate codes
              </Button>
            </div>

            <div>
              <h4 style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Smartphone size={16} /> Recovery contact (fallback if you lose your authenticator)
              </h4>
              <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
                <div>
                  <Label htmlFor="rec-email-live">Recovery email</Label>
                  <Input id="rec-email-live" type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} placeholder="you@personal.com" data-testid="2fa-live-email-input" />
                </div>
                <div>
                  <Label htmlFor="rec-phone-live">Recovery phone</Label>
                  <Input id="rec-phone-live" type="tel" value={recoveryPhone} onChange={(e) => setRecoveryPhone(e.target.value)} placeholder="+614XXXXXXXX" data-testid="2fa-live-phone-input" />
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={saveContact} style={{ marginTop: '0.5rem' }} data-testid="2fa-save-contact-btn">Save contact</Button>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ fontWeight: 600 }}>Trusted devices ({status.trusted_devices.length})</h4>
                {status.trusted_devices.length > 0 && (
                  <Button variant="outline" size="sm" onClick={revokeAllDevices} data-testid="2fa-revoke-all-btn">Revoke all</Button>
                )}
              </div>
              {status.trusted_devices.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No devices have been remembered yet. Tick &quot;Remember this device for 30 days&quot; at sign-in to skip 2FA on trusted browsers.</p>
              ) : (
                <div style={{ display: 'grid', gap: '0.4rem' }}>
                  {status.trusted_devices.map((d) => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }} data-testid={`2fa-device-row-${d.id}`}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{d.label || 'Browser'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          IP {d.ip} &middot; expires {new Date(d.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => revokeDevice(d.id)} data-testid={`2fa-revoke-device-${d.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ paddingTop: '1rem', borderTop: '1px dashed #e2e8f0' }}>
              {!showDisable ? (
                <Button variant="outline" onClick={() => setShowDisable(true)} data-testid="2fa-disable-btn">
                  <ShieldOff size={14} className="mr-1" /> Disable 2FA
                </Button>
              ) : (
                <div style={{ display: 'grid', gap: '0.75rem', maxWidth: '420px' }}>
                  <div style={{ color: '#dc2626', fontWeight: 600 }}>Confirm disable 2FA</div>
                  <Input type="password" placeholder="Your password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} data-testid="2fa-disable-password-input" />
                  <Input type="text" inputMode="numeric" placeholder="Current 6-digit authenticator code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="2fa-disable-code-input" />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="destructive" onClick={disable2FA} disabled={loading} data-testid="2fa-disable-confirm-btn">Disable 2FA</Button>
                    <Button variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(''); setDisableCode(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showRegen && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Regenerate recovery codes</div>
            <p style={{ fontSize: '0.85rem', color: '#92400e', marginBottom: '0.75rem' }}>
              This invalidates all 10 existing codes. Enter your current 6-digit authenticator code to confirm.
            </p>
            <Input type="text" inputMode="numeric" placeholder="123 456" value={regenCode} onChange={(e) => setRegenCode(e.target.value)} style={{ maxWidth: '200px', marginBottom: '0.75rem' }} data-testid="2fa-regen-code-input" />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={regenerateCodes} disabled={loading} data-testid="2fa-regen-confirm-btn">Regenerate</Button>
              <Button variant="outline" onClick={() => { setShowRegen(false); setRegenCode(''); }}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorPanel;
